import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SubscriptionStatus } from '../components/SubscriptionStatus';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calculator, 
  Package, 
  Wine, 
  MessageSquare,
  Plus,
  TrendingUp,
  FileText,
  ArrowRight,
  Sparkles,
  Target,
  Users,
  UserCheck,
  Camera,
  Clock,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import { DynamicIcon } from '../components/icons/DynamicIcon';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interesseEstoque, setInteresseEstoque] = useState(false);
  const [interesseFeatures, setInteresseFeatures] = useState({
    estoque: false,
    barbiz: false,
    barflow: false,
    drinkstock: false,
    barteam: false,
    socialbar: false
  });

  // Array de features futuras
  const featuresFuturas = [
    {
      id: 'barbiz',
      nome: 'BarBiz Canvas',
      subtitulo: 'BMC Interativo',
      descricao: 'Business Model Canvas digital específico para coquetelaria com templates prontos',
      preco: 'R$ 47/mês',
      icon: Target,
      cor: 'from-blue-50 to-indigo-50',
      iconCor: 'text-blue-600',
      beneficios: [
        'Preenchimento guiado',
        'Análise SWOT automática',
        'Exporta PDF profissional'
      ]
    },
    {
      id: 'barflow',
      nome: 'BarFlow CRM',
      subtitulo: 'Gestão de Clientes',
      descricao: 'Pipeline de vendas visual com automação de follow-up via WhatsApp',
      preco: 'R$ 17/mês',
      icon: Users,
      cor: 'from-green-50 to-emerald-50',
      iconCor: 'text-green-600',
      beneficios: [
        'Contratos digitais',
        'Agenda integrada',
        'NPS automático'
      ]
    },
    {
      id: 'drinkstock',
      nome: 'DrinkStock',
      subtitulo: 'Estoque Inteligente',
      descricao: 'Controle de estoque com previsão baseada em histórico de eventos',
      preco: 'R$ 37/mês',
      icon: Package,
      cor: 'from-purple-50 to-pink-50',
      iconCor: 'text-purple-600',
      beneficios: [
        'Alertas de reposição',
        'QR Code para inventário',
        'Cálculo de perdas'
      ]
    },
    {
      id: 'barteam',
      nome: 'BarTeam Hub',
      subtitulo: 'Gestão de Equipe',
      descricao: 'Escala automática e marketplace de bartenders verificados',
      preco: 'R$ 3/bartender',
      icon: UserCheck,
      cor: 'from-orange-50 to-amber-50',
      iconCor: 'text-orange-600',
      beneficios: [
        'Escala automática',
        'Controle de pagamentos',
        'Banco de freelancers'
      ]
    },
    {
      id: 'socialbar',
      nome: 'Social Bar Studio',
      subtitulo: 'Conteúdo com IA',
      descricao: 'Gerador de posts e reels com IA treinada em coquetelaria',
      preco: 'R$ 27/mês',
      icon: Camera,
      cor: 'from-pink-50 to-rose-50',
      iconCor: 'text-pink-600',
      beneficios: [
        'Posts com IA',
        'Templates de vídeo',
        'Agendamento automático'
      ]
    }
  ];

  const registrarInteresse = async (featureId) => {
    setInteresseFeatures(prev => ({
      ...prev,
      [featureId]: true
    }));
    
    // Salvar interesse no banco se quiser
    try {
      await supabase
        .from('feature_interests')
        .insert([{
          user_id: user?.id,
          feature_name: featureId,
          created_at: new Date()
        }]);
    } catch (err) {
      console.log('Erro ao salvar interesse:', err);
    }
    
    toast.success('Obrigado! Você será notificado quando lançarmos.');
  };

  useEffect(() => {
    fetchDrinks();
  }, []);

  const fetchDrinks = async () => {
    try {
      const { data, error } = await supabase
        .from('drinks')
        .select('*')
        .limit(6)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setDrinks(data);
      }
    } catch (err) {
      console.error('Erro ao buscar drinks:', err);
    } finally {
      setLoading(false);
    }
  };

  const registrarInteresseEstoque = async () => {
    setInteresseEstoque(true);
    // Aqui você pode salvar o interesse no banco
    toast.success('Obrigado! Avisaremos quando o módulo estiver disponível.');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
        <div className="mb-8">
          <SubscriptionStatus />
        </div>
        
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <Wine className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">EventPour</h1>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <DynamicIcon name="user" size={16} />
                Perfil
              </button>
              <span className="text-sm text-gray-600">{user?.email}</span>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Bem-vindo ao EventPour
          </h2>
          <p className="text-gray-600">
            Gerencie seus eventos e orçamentos de forma profissional
          </p>
        </div>

        {/* Quick Actions - Funcionalidades Básicas em Destaque */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/calcbar')}
            className="group bg-purple-600 hover:bg-purple-700 text-white rounded-xl p-6 transition-all transform hover:scale-105 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <Calculator className="w-10 h-10" />
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold mb-2">Calculadora de Orçamentos</h3>
            <p className="text-purple-100 text-sm">
              Crie orçamentos profissionais em minutos
            </p>
          </button>

          <button
            onClick={() => navigate('/drinks')}
            className="group bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-6 transition-all transform hover:scale-105 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <Wine className="w-10 h-10" />
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold mb-2">Drinks Populares</h3>
            <p className="text-blue-100 text-sm">
              Gerencie seu catálogo de drinks
            </p>
          </button>

          <button
            onClick={() => navigate('/feedback')}
            className="group bg-green-600 hover:bg-green-700 text-white rounded-xl p-6 transition-all transform hover:scale-105 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <MessageSquare className="w-10 h-10" />
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold mb-2">Enviar Feedback</h3>
            <p className="text-green-100 text-sm">
              Ajude-nos a melhorar o EventPour
            </p>
          </button>
        </div>

        {/* Vídeo Tutorial */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Como usar o EventPour</h3>
          <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden">
            <iframe
              className="w-full h-full"
              style={{ minHeight: '400px' }}
              src="https://www.youtube.com/embed/dQw4w9WgXcQ" // Substitua pelo ID real do seu vídeo
              title="Tutorial EventPour"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          
          {/* Passos rápidos abaixo do vídeo */}
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <p className="text-sm font-medium">Cadastre seus drinks</p>
              <p className="text-xs text-gray-600">Crie seu catálogo personalizado</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <p className="text-sm font-medium">Monte o orçamento</p>
              <p className="text-xs text-gray-600">Calcule automaticamente os valores</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <p className="text-sm font-medium">Envie para o cliente</p>
              <p className="text-xs text-gray-600">PDF profissional em segundos</p>
            </div>
          </div>
        </div>

        {/* Features Futuras */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Funcionalidades em Desenvolvimento</h3>
            <span className="text-sm text-gray-500">Ajude-nos a priorizar votando nas features</span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Gestão de Estoque (mantém a existente) */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">Gestão de Estoque</h3>
                    <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Em breve
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Controle seu estoque, receba alertas de reposição
                  </p>
                  <p className="text-xs font-semibold text-purple-600 mb-3">Grátis no lançamento</p>
                  {!interesseFeatures.estoque ? (
                    <button
                      onClick={() => registrarInteresse('estoque')}
                      className="text-sm px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                    >
                      Tenho interesse
                    </button>
                  ) : (
                    <p className="text-sm text-green-600 font-medium">✓ Interesse registrado</p>
                  )}
                </div>
              </div>
            </div>

            {/* Outras Features */}
            {featuresFuturas.map((feature) => (
              <div key={feature.id} className={`bg-gradient-to-r ${feature.cor} rounded-xl p-6 border border-gray-200`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <feature.icon className={`w-6 h-6 ${feature.iconCor}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">{feature.nome}</h3>
                      <span className="px-2 py-1 bg-gray-600 text-white text-xs rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        2025
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{feature.subtitulo}</p>
                    <p className="text-sm text-gray-600 mb-3">{feature.descricao}</p>
                    
                    {/* Benefícios */}
                    <ul className="text-xs text-gray-500 mb-3 space-y-1">
                      {feature.beneficios.map((beneficio, idx) => (
                        <li key={idx} className="flex items-center gap-1">
                          <Check className="w-3 h-3 text-green-500" />
                          {beneficio}
                        </li>
                      ))}
                    </ul>
                    
                    <p className="text-sm font-bold text-gray-900 mb-3">{feature.preco}</p>
                    
                    {!interesseFeatures[feature.id] ? (
                      <button
                        onClick={() => registrarInteresse(feature.id)}
                        className={`text-sm px-3 py-1 ${
                          feature.id === 'barbiz' ? 'bg-blue-600 hover:bg-blue-700' :
                          feature.id === 'barflow' ? 'bg-green-600 hover:bg-green-700' :
                          feature.id === 'drinkstock' ? 'bg-purple-600 hover:bg-purple-700' :
                          feature.id === 'barteam' ? 'bg-orange-600 hover:bg-orange-700' :
                          'bg-pink-600 hover:bg-pink-700'
                        } text-white rounded transition-colors`}
                      >
                        Quero ser avisado
                      </button>
                    ) : (
                      <p className="text-sm text-green-600 font-medium">✓ Você será avisado</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-8 bg-gray-900 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">🚀 Transforme seu Negócio de Bartending</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              O EventPour está evoluindo para se tornar a plataforma completa para profissionais de coquetelaria. 
              Vote nas funcionalidades que mais fariam diferença no seu negócio!
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="text-center">
                <p className="text-3xl font-bold">6</p>
                <p className="text-sm text-gray-400">Módulos planejados</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">
                  {Object.values(interesseFeatures).filter(Boolean).length}
                </p>
                <p className="text-sm text-gray-400">Seus votos</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">Q1 2025</p>
                <p className="text-sm text-gray-400">Lançamento previsto</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Preview */}
        <div className="mt-8 grid md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 text-center">
            <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-sm text-gray-600">Orçamentos</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <Wine className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{drinks.length}</p>
            <p className="text-sm text-gray-600">Drinks</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-sm text-gray-600">PDFs Gerados</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <MessageSquare className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-sm text-gray-600">Feedbacks</p>
          </div>
        </div>
      </main>
    </div>
  );
}